import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import InterventionTab from '../InterventionTab';
import SimulationOutput from './SimulationOutput';
import { api } from '../../api/client';
import HeadlineBand from '../engine/HeadlineBand';

/**
 * The shared Profit-at-Risk Monte Carlo console.
 *
 * Per V3 handoff §4 "dual-surface pattern":
 *   - Engine 5 / Profit-at-Risk owns this console (math, full controls, run log).
 *   - Engine 2 (Supply & Cost) currently renders the same component; V5 will
 *     split this into a read-mostly summary view that subscribes to the latest
 *     Engine 5 run.
 *
 * Until V5 split lands, "two surfaces, same logic" is satisfied by importing
 * from one source.
 */
export default function ProfitAtRiskConsole() {
  const [iterations, setIterations] = useState(5000);
  const [shocks, setShocks] = useState({ aluminium: 0.10, sweetener: 0.05, freight: 0.08, promo: 0.05 });
  const par = useMutation({ mutationFn: () => api.supplyProfitAtRisk(iterations, shocks) });

  return (
    <InterventionTab
      objective="O3 · Protect margin against correlated input shocks (defend 99¢ economics)"
      workflow={[
        'Set shock magnitudes per input (aluminium, sweetener, freight, promo)',
        'Choose iteration count (1k–50k)',
        'Run Monte Carlo against the spine',
        'Review p5/p50/p95 + hedge priority + supplier concentration',
      ]}
      decisionRules={{
        rules: [
          'Activate hedge if p5 < −$2M (margin floor breach)',
          'Re-diversify suppliers if single-supplier share > 55%',
        ],
        vetoes: ['N1 reprices_hero_can'],
      }}
      aiLogic={{
        description: 'Deterministic Monte Carlo with seeded random correlated shocks. No black-box ML — every percentile is explainable and reproducible.',
        modality: 'deterministic',
        hitlLevel: 'L4 human decision',
      }}
      kpi={{
        primary: 'p5 / p50 / p95 of profit distribution',
        downstream: 'Feeds hedge desk + treasury rebalancing',
        owner: 'CFO / Procurement',
      }}
      content={
        <div className="panel">
          <div className="qhead">
            <span className="qdot" style={{ background: 'var(--red)' }} />
            Run console · adjust shocks, then execute
          </div>
          {par.data && (
            <HeadlineBand
              view="profit-at-risk"
              state={par.data as unknown as Record<string, unknown>}
              readOnlyAction
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
            {(Object.keys(shocks) as Array<keyof typeof shocks>).map((k) => (
              <div key={k}>
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 14, textTransform: 'capitalize' }}>{k}</span>
                  <span className="mono" style={{ fontSize: 13, color: 'var(--amber)', fontWeight: 600 }}>+{(shocks[k] * 100).toFixed(0)}%</span>
                </div>
                <input className="slider" type="range" min={0} max={0.4} step={0.01} value={shocks[k]} onChange={(e) => setShocks((s) => ({ ...s, [k]: parseFloat(e.target.value) }))} />
              </div>
            ))}
          </div>

          <div className="row" style={{ gap: 12, marginBottom: 14 }}>
            <div style={{ flex: '0 0 200px' }}>
              <div className="small faint" style={{ marginBottom: 4, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Iterations</div>
              <select className="c2-input" value={iterations} onChange={(e) => setIterations(Number(e.target.value))}>
                <option value={1000}>1,000</option>
                <option value={5000}>5,000</option>
                <option value={10000}>10,000</option>
                <option value={50000}>50,000</option>
              </select>
            </div>
            <button type="button" className="btn primary" onClick={() => par.mutate()} disabled={par.isPending}>
              {par.isPending ? 'Running…' : '⚡ Run Monte Carlo'}
            </button>
          </div>

          {par.data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                <Quant label="p5 · downside (worst 5%)" value={par.data.p5} color="var(--red)" />
                <Quant label="p50 · median" value={par.data.p50} color="var(--gold)" />
                <Quant label="p95 · upside" value={par.data.p95} color="var(--olive)" />
              </div>
              <div style={{ height: 180, marginBottom: 14 }}>
                <ResponsiveContainer>
                  <BarChart data={[
                    { name: 'p5', value: par.data.p5 },
                    { name: 'p25', value: par.data.p5 + (par.data.p50 - par.data.p5) * 0.5 },
                    { name: 'p50', value: par.data.p50 },
                    { name: 'p75', value: par.data.p50 + (par.data.p95 - par.data.p50) * 0.5 },
                    { name: 'p95', value: par.data.p95 },
                  ]}>
                    <XAxis dataKey="name" stroke="var(--muted)" fontSize={12} />
                    <YAxis stroke="var(--muted)" fontSize={12} />
                    <Tooltip contentStyle={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13 }} />
                    <ReferenceLine y={0} stroke="var(--faint)" strokeDasharray="3 3" />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {[par.data.p5, 0, par.data.p50, 0, par.data.p95].map((v, i) => (
                        <Cell key={i} fill={v < 0 ? '#cf5440' : v < 2 ? '#d6a23a' : '#8aa658'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="panel" style={{ background: 'var(--panel2)' }}>
                <div className="small faint" style={{ marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 12 }}>Profit-at-Risk band</div>
                <div style={{ fontSize: 16, color: 'var(--red)', fontWeight: 600, marginBottom: 10 }}>{par.data.profit_at_risk_band}</div>
                <div className="small faint" style={{ marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 12 }}>Hedge priority · next dollar goes to</div>
                <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                  {par.data.hedge_priority.map((h, i) => (
                    <span key={h} className={`tag ${i === 0 ? 'amber' : 'muted'}`}>{i + 1}. {h}</span>
                  ))}
                </div>
                <div className="small" style={{ marginTop: 10 }}>
                  Single-supplier share: <b style={{ color: par.data.single_supplier_share > 0.55 ? 'var(--red)' : 'var(--olive)' }}>{(par.data.single_supplier_share * 100).toFixed(0)}%</b>
                  {par.data.single_supplier_share > 0.55 && ' (above 55% threshold — concentration risk)'}
                </div>
              </div>

              {/* The 4-part Move Brief (handoffV3 §10) */}
              <div style={{ marginTop: 14 }}>
                <ProfitAtRiskMoveBrief
                  p5={par.data.p5}
                  p50={par.data.p50}
                  p95={par.data.p95}
                  band={par.data.profit_at_risk_band}
                  hedgePriority={par.data.hedge_priority}
                  singleSupplierShare={par.data.single_supplier_share}
                />
              </div>
            </>
          )}
        </div>
      }
    />
  );
}

/* ───── Move Brief · the 4-part actionable output ───── */
function ProfitAtRiskMoveBrief({
  p5, p50, p95, band, hedgePriority, singleSupplierShare,
}: {
  p5: number; p50: number; p95: number; band: string;
  hedgePriority: string[]; singleSupplierShare: number;
}) {
  const needsHedge = p5 < -2;
  const topInput = hedgePriority[0] ?? 'aluminium';
  const concentrationFlag = singleSupplierShare > 0.55;
  // Decide-by: 2 weeks out (deterministic from today)
  const decideBy = new Date(Date.now() + 14 * 86400 * 1000).toISOString().slice(0, 10);

  const recommendation = needsHedge
    ? `Lock 60% of Q3 ${topInput} coverage today; layer freight floor at $4.80/gal next quarter.`
    : `Hold current hedge book at ${(0.42 * 100).toFixed(0)}% coverage; rebalance only if ${topInput} moves > +8%.`;

  const recommendationDetail = needsHedge
    ? `p5 of ${dollar(p5)} breaches the −$2M margin floor. ${topInput.charAt(0).toUpperCase() + topInput.slice(1)} is the highest-variance input. Hedging it removes ~70% of downside variance with ~$0.4M premium.`
    : `Median P&L ${dollar(p50)} with band ${dollar(p5)} → ${dollar(p95)}. No floor breach in current scenario.`;

  return (
    <SimulationOutput
      onDispose={async (d, _driftId, decision) => {
        // V4.1 — log a sim-derived decision; surfaces in the Learning Loop.
        // Default signal_id S-03 (competitor price slash / margin pressure)
        // is the closest demo-thread signal to a commodity / margin shock.
        await api.simulationDisposition({
          simulation_kind: 'profit_at_risk',
          signal_id: 'S-03',
          disposition: d,
          owner: 'CFO / Procurement',
          recommendation,
          predicted_outcome: {
            p5, p50, p95,
            hedge_target_input: topInput,
            single_supplier_share: singleSupplierShare,
            needs_hedge: needsHedge,
          },
          decision,
        });
      }}
      recommendation={recommendation}
      recommendationDetail={recommendationDetail}
      evidence={{
        headline: needsHedge
          ? `Active hedge cuts downside variance ~70% for ~$0.4M premium; protects 99¢ economics through Q3.`
          : `Margin floor holds in current shock envelope. Single-supplier share ${(singleSupplierShare * 100).toFixed(0)}% remains the dominant residual risk.`,
        band: { label: 'p5 → p95 (Monte Carlo, deterministic seed)', lo: dollar(p5), hi: dollar(p95) },
        bindingConstraintShift: concentrationFlag
          ? { from: 'M1 can supply (single-supplier)', to: 'M3 sweetener pass-through' }
          : undefined,
        exposedAssumption: needsHedge
          ? `Bets on ${topInput} reverting < 8% by September. If shock persists, hedge holds but inventory cushion compresses.`
          : `Bets on no >15% correlated freight + aluminium shock in the same quarter. Cross-correlation watched daily.`,
        metrics: [
          { label: 'p50 · median', value: dollar(p50), color: 'gold' },
          { label: band.includes('downside') ? 'p5 · downside' : 'p5', value: dollar(p5), color: p5 < 0 ? 'red' : 'olive' },
          { label: 'Concentration', value: `${(singleSupplierShare * 100).toFixed(0)}%`, color: concentrationFlag ? 'red' : 'olive' },
        ],
      }}
      alternatives={[
        {
          rank: 2,
          move: 'Do nothing · run on inventory cushion',
          expected: `p50 ${dollar(p50 - 0.4)}, full p5 ${dollar(p5)} downside if shock realizes`,
          whyLower: 'Leaves the margin floor exposed; surrenders the option to hedge cheap',
        },
        {
          rank: 3,
          move: 'Hedge 100% upfront across all inputs',
          expected: `p50 ${dollar(p50 - 0.9)}, p95 ${dollar(p95 - 1.4)} (capped upside)`,
          whyLower: 'Premium too high vs. variance bought; locks in unfavorable basis',
        },
      ]}
      blocked={[
        {
          move: 'Pass aluminium pass-through into hero-can retail price',
          vetoId: 'N1',
          vetoReason: 'reprices_hero_can — breaks the 99¢ economics covenant',
        },
      ]}
      actionPath={{
        owner: 'CFO / Procurement',
        decideBy,
        tripwire: `${topInput.charAt(0).toUpperCase() + topInput.slice(1)} futures > +12% intra-week OR freight on-time < 88%`,
        rollback: needsHedge
          ? 'Unwind hedge over 4-week ladder, reopen inventory-cushion play, restore CapEx for capacity Engine 6'
          : 'Activate hedge book within 5 trading days',
      }}
      ownerHint="entry appears in Learning Loop on next refresh"
    />
  );
}

function dollar(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '+';
  return `${sign}$${abs.toFixed(2)}M`;
}

function Quant({ label, value, color }: { label: string; value: number; color: string }) {
  const display = `${value > 0 ? '+' : ''}${value.toFixed(2)}M`;
  return (
    <div className="panel" style={{ padding: 14, background: 'var(--panel2)' }}>
      <div className="small faint" style={{ marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 12 }}>{label}</div>
      <div className="num" style={{ color, fontSize: 28 }}>{display}</div>
    </div>
  );
}
